// Exports: FilterBar
import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { FaSyncAlt } from "react-icons/fa";

const StatusFilterDropdown = ({
  open,
  statusFilter,
  setStatusFilter,
  setOpen,
}) =>
  open ? (
    <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border z-30 w-48">
      <div className="px-4 py-2 text-sm font-medium text-gray-700 border-b">
        Filter by Status
      </div>
      {["all", "Ongoing", "Completed", "Overdue"].map((status) => (
        <button
          key={status}
          className={clsx(
            "w-full text-left px-4 py-2 text-sm hover:bg-gray-100",
            statusFilter === status && "bg-purple-100 text-purple-700"
          )}
          onClick={() => {
            setStatusFilter(status);
            setOpen(false);
          }}
        >
          {status === "all" ? "All Statuses" : status}
        </button>
      ))}
    </div>
  ) : null;

const PriorityFilterDropdown = ({
  open,
  priorityFilter,
  setPriorityFilter,
  setOpen,
}) =>
  open ? (
    <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border z-30 w-48">
      <div className="px-4 py-2 text-sm font-medium text-gray-700 border-b">
        Filter by Priority
      </div>
      {["all", "High", "Medium", "Low"].map((priority) => (
        <button
          key={priority}
          className={clsx(
            "w-full text-left px-4 py-2 text-sm hover:bg-gray-100",
            priorityFilter === priority && "bg-purple-100 text-purple-700"
          )}
          onClick={() => {
            setPriorityFilter(priority);
            setOpen(false);
          }}
        >
          {priority === "all" ? "All Priorities" : priority}
        </button>
      ))}
    </div>
  ) : null;

const FilterBar = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  priorityFilter,
  setPriorityFilter,
  onRefresh,
  refreshing,
  onAddTaskGroupClick,
  canAddTaskGroup,
}) => {
  const filterRef = useRef(null);
  const [showFilters, setShowFilters] = useState(false);
  const [openStatusFilterDropdown, setOpenStatusFilterDropdown] =
    useState(false);
  const [openPriorityFilterDropdown, setOpenPriorityFilterDropdown] =
    useState(false);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!filterRef.current?.contains(event.target)) {
        setShowFilters(false);
        setOpenStatusFilterDropdown(false);
        setOpenPriorityFilterDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () =>
      document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <div className="py-2 flex flex-row justify-between px-5 items-center">
      <span className="text-lg xl:text-xl font-medium">Task Groups</span>

      <div className="flex items-center space-x-2">
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full pl-8 xl:pl-10 pr-3 xl:pr-4 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm xl:text-base"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>

        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setShowFilters((current) => !current)}
            className="flex items-center space-x-1 xl:space-x-2 px-2 xl:px-4 py-1.5 xl:py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-xs xl:text-sm"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span>Filters</span>
            {(statusFilter !== "all" || priorityFilter !== "all") && (
              <span className="ml-1 w-2 h-2 bg-red-500 rounded-full" />
            )}
          </button>

          {showFilters && (
            <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-lg border p-4 w-80 z-30">
              <div className="space-y-3">
                <div className="relative">
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenStatusFilterDropdown((current) => !current);
                    }}
                    className="w-full flex justify-between items-center p-2 border rounded hover:bg-gray-50"
                  >
                    <span>
                      {statusFilter === "all" ? "All Statuses" : statusFilter}
                    </span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <StatusFilterDropdown
                    open={openStatusFilterDropdown}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    setOpen={setOpenStatusFilterDropdown}
                  />
                </div>

                <div className="relative">
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenPriorityFilterDropdown((current) => !current);
                    }}
                    className="w-full flex justify-between items-center p-2 border rounded hover:bg-gray-50"
                  >
                    <span>
                      {priorityFilter === "all"
                        ? "All Priorities"
                        : priorityFilter}
                    </span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <PriorityFilterDropdown
                    open={openPriorityFilterDropdown}
                    priorityFilter={priorityFilter}
                    setPriorityFilter={setPriorityFilter}
                    setOpen={setOpenPriorityFilterDropdown}
                  />
                </div>

                {(statusFilter !== "all" || priorityFilter !== "all") && (
                  <button
                    onClick={() => {
                      setStatusFilter("all");
                      setPriorityFilter("all");
                    }}
                    className="w-full p-2 text-sm text-red-600 hover:bg-red-50 rounded"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <button
          onClick={onRefresh}
          className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded px-2 xl:px-4 py-1.5 xl:py-2 text-xs xl:text-sm flex items-center"
          disabled={refreshing}
        >
          <FaSyncAlt className={`mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>

        {canAddTaskGroup && (
          <button
            onClick={onAddTaskGroupClick}
            className="bg-btn-bg hover:bg-purple-800 text-white rounded px-2 xl:px-4 py-1.5 xl:py-2 text-xs xl:text-sm flex items-center"
          >
            <span className="mr-1 font-bold">+</span> Add Task Group
          </button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;
